import { useQuery } from "@tanstack/react-query";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { FileText, Printer } from "lucide-react";
import { useMemo } from "react";

export default function Settings() {
  const { user } = useAuth();
  const { data: patients = [] } = useQuery({
    queryKey: ["patients"],
    queryFn: async () => (await http.get("/patients")).data,
  });

  const report = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const inMonth = patients.filter((p) => new Date(p.enquiry_date) >= monthStart);
    const conv = inMonth.filter((p) => p.enquiry_status === "Converted to Treatment").length;
    const revenue = inMonth.reduce((a, p) => a + (Number(p.revenue) || 0), 0);
    const rate = inMonth.length ? Math.round((conv / inMonth.length) * 100) : 0;
    const bySource = {};
    for (const p of inMonth) bySource[p.acquisition_source] = (bySource[p.acquisition_source] || 0) + 1;
    return { inMonth, conv, revenue, rate, bySource, month: now.toLocaleString("default", { month: "long", year: "numeric" }) };
  }, [patients]);

  const printReport = () => {
    const w = window.open("", "_blank");
    const bySource = Object.entries(report.bySource).map(([k, v]) => `<tr><td>${k}</td><td style="text-align:right">${v}</td></tr>`).join("");
    w.document.write(`
      <html><head><title>PatientFlow Monthly Report — ${report.month}</title>
      <style>
        body{font-family:-apple-system,Arial,sans-serif;padding:32px;color:#18181B;max-width:720px;margin:auto}
        h1{font-size:22px;margin:0 0 4px}h2{font-size:16px;margin:24px 0 8px;color:#059669}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        td,th{padding:8px 10px;border-bottom:1px solid #E4E4E7;font-size:13px}
        .kpi{display:inline-block;padding:12px 16px;border:1px solid #E4E4E7;border-radius:8px;margin-right:8px;margin-bottom:8px;min-width:120px}
        .kpi .n{font-size:22px;font-weight:600}
        .muted{color:#71717A;font-size:12px}
      </style></head><body>
      <h1>${user?.clinic_name || "PatientFlow"} — Monthly Report</h1>
      <div class="muted">${report.month} · Physiotherapist: ${user?.physio_name || ""} · ${user?.city || ""}</div>
      <h2>Summary</h2>
      <div>
        <div class="kpi"><div class="muted">Enquiries</div><div class="n">${report.inMonth.length}</div></div>
        <div class="kpi"><div class="muted">Converted</div><div class="n">${report.conv}</div></div>
        <div class="kpi"><div class="muted">Conversion Rate</div><div class="n">${report.rate}%</div></div>
        <div class="kpi"><div class="muted">Revenue</div><div class="n">₹ ${report.revenue.toLocaleString()}</div></div>
      </div>
      <h2>Acquisition Source Breakdown</h2>
      <table><thead><tr><th style="text-align:left">Source</th><th style="text-align:right">Patients</th></tr></thead>
      <tbody>${bySource || '<tr><td colspan="2" class="muted">No records this month</td></tr>'}</tbody></table>
      <p class="muted" style="margin-top:32px">Generated by PatientFlow · Beta</p>
      </body></html>
    `);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-zinc-900">Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Your clinic profile, exports, and beta info.</p>
      </div>

      <Card className="p-5 border-zinc-200">
        <h3 className="font-heading text-base font-semibold text-zinc-900">Clinic profile</h3>
        <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
          <Info label="Clinic Name" value={user?.clinic_name} />
          <Info label="Physiotherapist" value={user?.physio_name} />
          <Info label="City" value={user?.city} />
          <Info label="Email" value={user?.email} />
          <Info label="Phone" value={user?.phone || "—"} />
          <Info label="Clinic Start Date" value={user?.clinic_start_date || "—"} />
        </div>
      </Card>

      <Card className="p-5 border-zinc-200">
        <h3 className="font-heading text-base font-semibold text-zinc-900">Monthly report</h3>
        <p className="text-sm text-zinc-500 mt-1">A printable summary for {report.month} — great for your monthly review.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={printReport} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="print-report-btn">
            <Printer className="h-4 w-4" /> Download Monthly Report
          </Button>
        </div>
      </Card>

      <Card className="p-5 border-zinc-200 bg-emerald-50/40">
        <div className="flex gap-3 items-start">
          <FileText className="h-5 w-5 text-emerald-700 mt-0.5" />
          <div className="text-sm text-emerald-900">
            <div className="font-medium">PatientFlow is currently in beta.</div>
            Your feedback will help us improve the tool for physiotherapists. Use the &quot;Give Feedback&quot; button in the sidebar anytime.
          </div>
        </div>
      </Card>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
      <div className="mt-1 text-zinc-800">{value}</div>
    </div>
  );
}
